import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useT, Bi, TKey } from "@/lib/i18n";
import { inr, todayISO } from "@/lib/format";
import { BottomSheet } from "@/components/BottomSheet";
import { Field, BigInput, MoneyInput } from "@/components/form";

function Row({ k, v, tone = "", testId }: { k: TKey; v: number; tone?: string; testId: string }) {
  const { t } = useT();
  return (
    <div className="flex justify-between py-1.5 text-sm">
      <span className="text-slate-600">{t(k)}</span>
      <b data-testid={testId} className={`font-mono-num ${tone}`}>{inr(v)}</b>
    </div>
  );
}

export function MoveOutSheet({ tenant, open, onClose, onDone }: { tenant: any; open: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const [date, setDate] = useState(todayISO());
  const [deds, setDeds] = useState<{ label: string; amount: number }[]>([]);
  const [adjust, setAdjust] = useState(true);
  const [calc, setCalc] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const body = { move_out_date: date, deductions: deds.filter((d) => d.label.trim()), adjust_against_rent: adjust };
  const key = JSON.stringify(body);
  useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => {
      api.post(`/tenants/${tenant.id}/settlement/preview`, body).then((r) => setCalc(r.data)).catch((e) => toast.error(errMsg(e)));
    }, 300);
    return () => clearTimeout(id);
  }, [key, open]); // eslint-disable-line react-hooks/exhaustive-deps
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/tenants/${tenant.id}/move-out`, body);
      toast.success(t("moveout_done"));
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title={<Bi k="moveout_title" />} description={tenant.name} testId="moveout-sheet">
      <div className="space-y-4">
        <Field label={t("moveout_date")}><BigInput data-testid="moveout-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label={t("deductions")}>
          <div className="space-y-2">
            {deds.map((d, i) => (
              <div key={i} className="flex gap-2">
                <BigInput data-testid={`moveout-ded-label-${i}`} placeholder={t("deduction_label")} value={d.label} onChange={(e) => setDeds(deds.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                <div className="w-32 shrink-0"><MoneyInput data-testid={`moveout-ded-amount-${i}`} value={d.amount} onChange={(v) => setDeds(deds.map((x, j) => (j === i ? { ...x, amount: v } : x)))} /></div>
                <button data-testid={`moveout-ded-remove-${i}`} onClick={() => setDeds(deds.filter((_, j) => j !== i))} className="h-12 w-10 grid place-items-center text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button data-testid="moveout-add-ded-btn" onClick={() => setDeds([...deds, { label: "", amount: 0 }])} className="h-11 px-4 rounded-full border border-dashed border-slate-300 text-sm font-semibold text-slate-600 flex items-center gap-1 pg-tap">
              <Plus className="h-4 w-4" />{t("add_deduction")}
            </button>
          </div>
        </Field>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <span className="text-sm font-semibold"><Bi k="adjust_deposit" /></span>
          <Switch data-testid="moveout-adjust-switch" checked={adjust} onCheckedChange={setAdjust} />
        </label>
        {calc && (
          <div className="rounded-2xl border border-slate-200 p-4 divide-y divide-slate-100" data-testid="moveout-summary">
            <div className="pb-2">
              <Row k="deposit" v={calc.deposit} testId="moveout-deposit" />
              <Row k="deductions" v={-calc.deductions_total} testId="moveout-deductions" />
              <Row k="rent_outstanding" v={calc.rent_outstanding} tone="text-rose-600" testId="moveout-outstanding" />
              <Row k="deposit_applied" v={calc.deposit_applied_to_rent} testId="moveout-applied" />
            </div>
            <div className="pt-2">
              <Row k="refund" v={calc.refund_to_tenant} tone="text-emerald-700 text-lg" testId="moveout-refund" />
              {calc.tenant_still_owes > 0 && <Row k="still_owes" v={calc.tenant_still_owes} tone="text-rose-600" testId="moveout-still-owes" />}
            </div>
          </div>
        )}
        <Button data-testid="moveout-confirm-btn" disabled={busy || !calc} onClick={confirm} className="w-full h-14 rounded-xl text-base bg-rose-600 hover:bg-rose-700">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("confirm_moveout")}
        </Button>
      </div>
    </BottomSheet>
  );
}
